import React, { useState, useEffect } from 'react';
import './ContactUs.css';

const ContactUs = () => {
  const [selectedServicesFromQuote, setSelectedServicesFromQuote] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    isdCode: '+91',
    helpWith: '',
    additionalInfo: '',
    agreeToPrivacy: false
  });

  const [errors, setErrors] = useState({});
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  // OTP state
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [triggerMessage, setTriggerMessage] = useState('');
  const [isTriggerDone, setIsTriggerDone] = useState(false);

  // Request tracking (no CSRF/cookies)
  const [requestId, setRequestId] = useState('');
  const [, setIsBootstrapReady] = useState(false);

  // No cookie access required

  const generateVerificationCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setVerificationCode(code);
  };

  // Load selected services from sessionStorage when component mounts
  useEffect(() => {
    generateVerificationCode();
    const savedServices = sessionStorage.getItem('selectedServices');
    if (savedServices) {
      try {
        const services = JSON.parse(savedServices);
        setSelectedServicesFromQuote(services);
        
        // Pre-fill the help option if services were selected
        if (services.length > 0) {
          setFormData(prev => ({
            ...prev,
            helpWith: 'Custom Quote Request',
            additionalInfo: prev.additionalInfo + 
              (prev.additionalInfo ? '\n\n' : '') +
              'Selected Services from Quote:\n' +
              services.map(service => `• ${service.name}`).join('\n')
          }));
        }
        
        // Clear the sessionStorage after using it
        sessionStorage.removeItem('selectedServices');
      } catch (error) {
        console.error('Error parsing selected services:', error);
      }
    }
  }, []);

  // Resend OTP countdown
  useEffect(() => {
    if (!isOtpOpen || resendSeconds <= 0) return;
    const timer = setInterval(() => {
      setResendSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [isOtpOpen, resendSeconds]);

  const API_ROOT = 'https://newsite.sysmaco.in:8091';
  const BOOTSTRAP_URL = `${API_ROOT}/api/sendemail/bootstrap`;
  const GENERATE_OTP_URL = `${API_ROOT}/api/sendemail/generateopt`;
  const REGENERATE_OTP_URL = `${API_ROOT}/api/sendemail/regenerateopt`;
  const TRIGGER_URL = `${API_ROOT}/api/sendemail/trigger`;

  // Bootstrap on mount to receive request id (no cookies)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.__SYS_EMAIL_BOOTSTRAP_DONE__) return;
      window.__SYS_EMAIL_BOOTSTRAP_DONE__ = true;
    }
    const init = async () => {
      try {
        const resp = await fetch(BOOTSTRAP_URL, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-store'
        });
        const reqId = resp.headers.get('X-Request-ID') || resp.headers.get('x-request-id') || '';
        if (reqId) setRequestId(reqId);
        if (reqId) setIsBootstrapReady(true);
      } catch (err) {
        console.error('Bootstrap call failed:', err);
      }
    };
    init();
  }, [BOOTSTRAP_URL]);

  // Ensure we have X-Request-ID before protected calls
  const ensureBootstrap = async () => {
    if (requestId) return requestId;
    try {
      const resp = await fetch(BOOTSTRAP_URL, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store'
      });
      const reqId = resp.headers.get('X-Request-ID') || resp.headers.get('x-request-id') || '';
      if (reqId) {
        setRequestId(reqId);
        setIsBootstrapReady(true);
      }
      return reqId;
    } catch (err) {
      console.error('Bootstrap ensure failed:', err);
      return '';
    }
  };

  const indianISDCodes = [
    { code: '+91', country: 'India' }
  ];

  const helpOptions = [
    'Custom Quote Request',
    'Hardware Installation & Setup',
    'Network Configuration & Maintenance',
    'Technical Support & Troubleshooting',
    'Server Management & Monitoring',
    'Data Recovery Services',
    'Cybersecurity Solutions',
    'Cloud Migration & Setup',
    'IT Consultation & Planning',
    'Software Installation & Updates',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company Name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required';
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.helpWith) {
      newErrors.helpWith = 'Please select how we can help you';
    }

    if (!formData.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
    }

    if (!enteredCode.trim()) {
      newErrors.verification = 'Enter the verification code';
    } else if (enteredCode.trim() !== verificationCode) {
      newErrors.verification = 'Verification code does not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const rid = await ensureBootstrap();
      if (!rid) {
        alert('Initialization failed. Please reload the page and try again.');
        return;
      }
      setIsSendingOtp(true);
      setOtp('');
      setOtpError('');

      const payload = {
        ...formData,
        selectedServices: selectedServicesFromQuote
      };

      const resp = await fetch(`${GENERATE_OTP_URL}?emailType=contact_us`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': rid
        },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || 'Failed to send OTP');
      }
      setTriggerMessage('');
      setIsTriggerDone(false);
      setIsOtpOpen(true);
      setResendSeconds(60);
    } catch (err) {
      alert('Could not send OTP. Please try again later.');
      console.error('Send OTP failed:', err);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtpAndSubmit = async () => {
    if (!otp || otp.trim().length !== 6) {
      setOtpError('Enter the 6-digit OTP');
      return;
    }
    setOtpError('');
    try {
      setIsVerifyingOtp(true);
      const rid = await ensureBootstrap();
      if (!rid) {
        setIsVerifyingOtp(false);
        setOtpError('Initialization failed. Please reload and try again.');
        return;
      }
      const triggerResp = await fetch(`${TRIGGER_URL}?emailType=contact_us&otp=${encodeURIComponent(otp.trim())}`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'X-Request-ID': rid
        }
      });
      if (!triggerResp.ok) {
        const errText = await triggerResp.text();
        throw new Error(errText || 'OTP verification failed');
      }

      let message = 'Email being shared with Support Team';
      try {
        const data = await triggerResp.json();
        if (data && data.message) message = data.message;
      } catch (_) {}

      setTriggerMessage(message);
      setIsTriggerDone(true);

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        isdCode: '+91',
        helpWith: '',
        additionalInfo: '',
        agreeToPrivacy: false
      });
      setErrors({});
      setEnteredCode('');
      generateVerificationCode();
      setSelectedServicesFromQuote([]);
      setOtp('');
      setResendSeconds(0);
    } catch (err) {
      console.error('Verify/Submit failed:', err);
      setOtpError('OTP verification failed');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || isSendingOtp) return;
    try {
      setIsSendingOtp(true);
      setOtp('');
      setOtpError('');
      const rid = await ensureBootstrap();
      if (!rid) {
        setIsSendingOtp(false);
        setOtpError('Initialization failed. Please reload and try again.');
        return;
      }
      const resp = await fetch(`${REGENERATE_OTP_URL}?emailType=contact_us`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'X-Request-ID': rid
        }
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || 'Failed to resend OTP');
      }
      setResendSeconds(60);
    } catch (err) {
      console.error('Resend OTP failed:', err);
      setOtpError('Could not resend OTP. Try again later.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="page-title">Contact Us</h1>
            <p className="page-description">
              Get in touch with our IT experts. We're here to help you with all your technology needs.
            </p>
          </div>
        </div>
      </div>

      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Get In Touch</h2>
              <p>Ready to transform your IT infrastructure? Let's discuss your requirements and find the perfect solution for your business.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">📧</div>
                  <div>
                    <h4>Email</h4>
                    <p><a href="mailto:info@sysmaco.in">info@sysmaco.in</a></p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div>
                    <h4>Phone</h4>
                    <p><a href='tel:+91 (33) 35531279' >+91 (33) 35531279</a></p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div>
                    <h4>Office</h4>
                    <p>32A, Mahanirban Road, Kolkata-700029</p>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">⏰</div>
                  <div>
                    <h4>Business Hours</h4>
                    <p>Mon - Fri: 10:30 AM - 6:30 PM </p>
                    <p className='sat-hours'>Sat: 10:30 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Google Maps Section */}
              <div className="map-section">
                <h3>Find Us</h3>
                <div className="map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d230.34879042628037!2d88.35963764152201!3d22.519911754937674!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0277372814e0b3%3A0x45910764e528d779!2sSysmaco!5e0!3m2!1sen!2sin!4v1755796327725!5m2!1sen!2sin"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sysmaco Office Location - 32A, Mahanirban Road, Kolkata-700029"
                  ></iframe>
                </div>
                <div className="map-actions">
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=32A+Mahanirban+Road+Kolkata+700029"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    Open in Google Maps
                  </a>
                  <a 
                    href="https://www.google.com/maps/dir/?api=1&destination=32A+Mahanirban+Road+Kolkata+700029"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <h2>Send Us a Message</h2>
                
                {selectedServicesFromQuote.length > 0 && (
                  <div className="quote-services-info">
                    <h3>📋 Quote Request for Selected Services</h3>
                    <div className="selected-quote-services">
                      {selectedServicesFromQuote.map((service) => (
                        <span key={service.id} className="quote-service-tag">
                          {service.name}
                        </span>
                      ))}
                    </div>
                    <p className="quote-info-text">
                      Your selected services have been pre-filled below. Feel free to add more details!
                    </p>
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={errors.firstName ? 'error' : ''}
                      aria-invalid={!!errors.firstName}
                      aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                      required
                    />
                    {errors.firstName && <span id="firstName-error" className="error-message">{errors.firstName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={errors.lastName ? 'error' : ''}
                      aria-invalid={!!errors.lastName}
                      aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                      required
                    />
                    {errors.lastName && <span id="lastName-error" className="error-message">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    required
                  />
                  {errors.email && <span id="email-error" className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className={errors.companyName ? 'error' : ''}
                    aria-invalid={!!errors.companyName}
                    aria-describedby={errors.companyName ? 'companyName-error' : undefined}
                    required
                  />
                  {errors.companyName && <span id="companyName-error" className="error-message">{errors.companyName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number *</label>
                  <div className="phone-input">
                    <select
                      name="isdCode"
                      value={formData.isdCode}
                      onChange={handleInputChange}
                      className="isd-select"
                    >
                      {indianISDCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.code} ({item.country})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                      className={errors.phoneNumber ? 'error' : ''}
                      aria-invalid={!!errors.phoneNumber}
                      aria-describedby={errors.phoneNumber ? 'phoneNumber-error' : undefined}
                      required
                    />
                  </div>
                  {errors.phoneNumber && <span id="phoneNumber-error" className="error-message">{errors.phoneNumber}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="helpWith">How Can We Help? *</label>
                  <select
                    id="helpWith"
                    name="helpWith"
                    value={formData.helpWith}
                    onChange={handleInputChange}
                    className={errors.helpWith ? 'error' : ''}
                    aria-invalid={!!errors.helpWith}
                    aria-describedby={errors.helpWith ? 'helpWith-error' : undefined}
                    required
                  >
                    <option value="">Select a service</option>
                    {helpOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {errors.helpWith && <span id="helpWith-error" className="error-message">{errors.helpWith}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="additionalInfo">Please Feel Free To Tell Us A Little More</label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Share details about your requirements, current setup, or any specific challenges you're facing..."
                  />
                </div>

                <div className="form-group verification-group">
                  <label htmlFor="verificationInput">Verification Code *</label>
                  <div className="verification-row">
                    <div className="verification-code" aria-label="Verification code" role="img">{verificationCode}</div>
                    <button type="button" className="btn btn-secondary refresh-code" onClick={generateVerificationCode} aria-label="Refresh verification code">↻</button>
                  </div>
                  <input
                    type="text"
                    id="verificationInput"
                    name="verification"
                    value={enteredCode}
                    onChange={(e) => {
                      setEnteredCode(e.target.value);
                      if (errors.verification) {
                        setErrors(prev => ({ ...prev, verification: '' }));
                      }
                    }}
                    placeholder="Type the code shown above"
                    className={errors.verification ? 'error' : ''}
                    aria-invalid={!!errors.verification}
                    aria-describedby={errors.verification ? 'verification-error' : undefined}
                    required
                  />
                  {errors.verification && <span id="verification-error" className="error-message">{errors.verification}</span>}
                </div>

                {/* OTP is handled after form validation in a modal */}

                <div className="form-group checkbox-group">
                  <label className="checkbox-label" htmlFor="agreeToPrivacy">
                    <input
                      id="agreeToPrivacy"
                      type="checkbox"
                      name="agreeToPrivacy"
                      checked={formData.agreeToPrivacy}
                      onChange={handleInputChange}
                      className={errors.agreeToPrivacy ? 'error' : ''}
                    />
                    <span className="checkmark"></span>
                    <span className="checkbox-text">
                      By submitting this form, you agree to share your information with us for tracking, analytics, and communication purposes. For more details on how we use your data, please review our <a href="#privacy-policy" onClick={(e) => { e.preventDefault(); setIsPrivacyOpen(true); }}>Privacy Policy</a>
                    </span>
                  </label>
                  {errors.agreeToPrivacy && <span className="error-message">{errors.agreeToPrivacy}</span>}
                </div>

                <button type="submit" className="btn btn-primary submit-btn" disabled={isSendingOtp} aria-disabled={isSendingOtp}>
                  {isSendingOtp ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    {isPrivacyOpen && (
      <div className="modal-overlay" onClick={() => setIsPrivacyOpen(false)}>
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3 id="privacy-title" className="modal-title">Privacy Policy</h3>
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => setIsPrivacyOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="modal-body" id="privacy-body">
            <p>
              We collect the information you voluntarily provide in this form (such as your name, company, email address, and phone number) to respond to your inquiry, provide quotes, and follow up on related services.
            </p>
            <p>
              We may use basic analytics to understand interest in our services and improve our offerings. We do not sell your personal information. We only share data with trusted service providers necessary to operate and communicate with you.
            </p>
            <p>
              You can request access, correction, or deletion of your information by contacting us at <a href="mailto:info@sysmaco.in">info@sysmaco.in</a>. We retain your information only as long as needed for the purposes stated above or as required by law.
            </p>
            <p>
              By submitting this form, you consent to this use of your information.
            </p>
          </div>
        </div>
      </div>
    )}

    {isOtpOpen && (
      <div className="modal-overlay" onClick={() => setIsOtpOpen(false)}>
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otp-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3 id="otp-title" className="modal-title">{isTriggerDone ? 'Success' : 'Enter OTP'}</h3>
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={() => setIsOtpOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            {!isTriggerDone ? (
              <>
                <p>We sent a 6-digit code to {formData.email}. Enter it below to verify.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\\d{6}"
                  maxLength="6"
                  className={`opt-input-field otp-input${otpError ? ' error' : ''}`} 
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/[^0-9]/g, ''));
                    if (otpError) setOtpError('');
                  }}
                  placeholder="Enter 6-digit OTP"
                  aria-label="One-time password"
                />
                {otpError && <div className="error-message" role="alert">{otpError}</div>}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary opt-btn"
                    onClick={handleResendOtp}
                    disabled={resendSeconds > 0 || isSendingOtp}
                    aria-disabled={resendSeconds > 0 || isSendingOtp}
                    title={resendSeconds > 0 ? `Resend available in ${resendSeconds}s` : 'Resend OTP'}
                  >
                    {isSendingOtp ? 'Sending...' : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend OTP'}
                  </button>
                  &nbsp;
                  <button
                    type="button"
                    className="btn btn-primary opt-btn"
                    onClick={handleVerifyOtpAndSubmit}
                    disabled={isVerifyingOtp}
                  >
                    {isVerifyingOtp ? 'Verifying...' : 'Verify & Submit'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>{triggerMessage || 'Email being shared with Support Team'}</p>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-primary opt-btn"
                    onClick={() => setIsOtpOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default ContactUs;
